import React, { useEffect, useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import InvoiceTable from '../../components/VisitDetails/invoiceTable';
import Modal from '../../components/modal/Modal';
import BulkJournalModal from '../../components/modal/BulkJournalModal';
import './VisitDetail.css';
import { Clock } from 'lucide-react';

import ModernInfoPopup from '../../components/VisitDetails/ModernInfoPopup';
import ChargeCapturePanel from '../../components/VisitDetails/ChargeCapturePanel';
import DiscountPanel from '../../components/VisitDetails/DiscountPanel';
import JournalPanel from '../../components/VisitDetails/JournalPanel';
import NoteHeaderContent from '../../components/VisitDetails/NoteHeaderContent';
import NoteModalContent from '../../components/VisitDetails/NoteModalContent';
import StatCardMinis from '../../components/VisitDetails/StatCardMinis';
import StatCards from '../../components/VisitDetails/StatCards';

import { useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';

import { getApiBaseUrl, getAuthConfig } from '../../services/configService';

const VisitDetail = () => {
  const { user } = useContext(UserContext);

  const { unique_id } = useParams();          
  const navigate = useNavigate();
  const location = useLocation();

  const visitFromState = location.state?.visit;
  const returnSearch = location.state?.returnSearch;
  const [visitData, setVisitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalNote, setModalNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('notes');
  const [filterCriteria, setFilterCriteria] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [activeInfoItem, setActiveInfoItem] = useState(null);
  const [openBulkJournal, setOpenBulkJournal] = useState(false);

  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const getAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const response = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return response.accessToken;
    } catch {
      const response = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return response.accessToken;
    }
  };

  const normalizeDateKey = (date) => {
    if (!date) return null;
  
    let d;
  
    if (typeof date === 'string') {
      if (!date.includes('Z') && !date.includes('+')) {
        d = new Date(date.replace(' ', 'T') + 'Z');
      } else {
        d = new Date(date);
      }
    } else {
      d = new Date(date);
    }
  
    return d.toISOString().slice(0, 19);
  };  

  const fetchVisitDetails = async ({ silent = false } = {}) => {
    if (!unique_id) return;
  
    try {
      if (!silent) {
        setLoading(true); 
      }
  
      const token = await getAccessToken();
      if (!token) return;
  
      const res = await fetch(
        `${getApiBaseUrl()}/visit-details?unique_id=${encodeURIComponent(unique_id)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const json = await res.json();
      setVisitData(json);
    } catch (err) {
      console.error('Error fetching visit details:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  
  useEffect(() => {
    fetchVisitDetails();
  }, [unique_id]);

  const headerVisit = visitFromState || {};
  const fullVisit = visitData?.data || {};
  const lineItems = fullVisit.invoice || [];
  const medNotesRaw = fullVisit.medical_notes || [];
  const captureData = fullVisit.charge_capture || [];
  const discountData = fullVisit.discounts || [];
  const journalData = fullVisit.journals || [];
  const statusData = fullVisit.card_status || {};

  const journalIndex = useMemo(() => {
    const index = {
      byInvoiceItemId: new Map(),
      byLabel: new Set(),
      byInvoiceKey: new Set(),
    };
  
    for (const j of journalData) {
      if (!j || !j.unique_id) continue;
  
      if (j.invoiceitemid) {
        index.byInvoiceItemId.set(String(j.invoiceitemid), j);
      }
  
      if (typeof j.label === 'string' && j.label.trim()) {
        index.byLabel.add(j.label.trim());
      }
  
      if (j.invoiceitemid && j.completed_date) {
        const key = [
          j.unique_id,
          j.invoiceitemid,
          normalizeDateKey(j.completed_date),
        ].join('|');
  
        index.byInvoiceKey.add(key);
      }
    }
  
    return index;
  }, [journalData]);

  const checkInInfo = useMemo(() => {
    if (!headerVisit?.check_in_at) return null;
  
    const checkInDate = new Date(headerVisit.check_in_at);
    if (isNaN(checkInDate)) return null;
  
    const now = new Date();
    const diffMs = now - checkInDate;
    if (diffMs < 0) return null;
  
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
    const fullBlocks = Math.floor(diffHours / 12);
    const remainingHours = diffHours % 12;
  
    const decimalBlocks = (diffHours / 12).toFixed(1);
  
    return {
      formattedDate: checkInDate.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      decimalBlocks,
      remainingHours,
    };
  }, [headerVisit?.check_in_at]);

  const medNotes = useMemo(() => {
    return medNotesRaw
      .filter(n => typeof n.text_only === 'string' && n.text_only.trim())
      .sort((a, b) => new Date(b.createdate) - new Date(a.createdate));
  }, [medNotesRaw]);

  const dropdownResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const lower = searchTerm.toLowerCase();

    return medNotes
      .filter(note => note.text_only?.toLowerCase().includes(lower))
      .map(note => {
        const text = note.text_only;
        const matchIndex = text.toLowerCase().indexOf(lower);
        const before = text.lastIndexOf('\r\n', matchIndex);
        const after = text.indexOf('\r\n', matchIndex);

        return {
          ...note,
          snippet: text.substring(
            before === -1 ? 0 : before + 2,
            after === -1 ? text.length : after
          ),
        };
      });
  }, [searchTerm, medNotes]);
  
  const updateCardStatusLocally = (newStatus) => {
    setVisitData(prev => {
      if (!prev) return prev;
  
      return {
        ...prev,
        data: {
          ...prev.data,
          card_status: newStatus,
        },
      };
    });
  };

  const handleMissedChargeClick = () => {
    setSelectedCard(prev => (prev === 'missed' ? null : 'missed'));
    setFilterCriteria(prev =>
      prev
        ? null
        : { from_itp: true, from_pvl: false, is_voided: false }
    );
  };

  const handleVoidedItemsClick = () => {
    setSelectedCard(prev => (prev === 'voided' ? null : 'voided'));
    setFilterCriteria(prev =>
      prev ? null : { is_voided: true }
    );
  };

  const renderHighlightedText = (text, highlights = []) => {
    if (!highlights.length) return text;
  
    const sorted = [...highlights].sort((a, b) => a.start - b.start);
    const parts = [];
    let lastIndex = 0;
  
    sorted.forEach((h, i) => {
      if (h.start > lastIndex) {
        parts.push(text.slice(lastIndex, h.start));
      }
  
      parts.push(
        <span
          key={`${h.id}-${i}`}
          className="highlightText"
          data-id={h.id}
        >
          {text.slice(h.start, h.end)}
        </span>
      );
  
      lastIndex = h.end;
    });
  
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
  
    return parts;
  };

  const handleChargeCaptureClick = () => {
    if (!captureData.length) return;

    if (selectedCard === 'capture') {
      setSelectedCard(null);
      setFilterCriteria(null);
      return;
    }

    const failedCodes = captureData
      .filter(item => item.result === 'FAIL')
      .flatMap(item =>
        item.codes_to_check_for.split(',').map(code => code.trim())
      );

    setSelectedCard('capture');
    setFilterCriteria({ pims_ids: failedCodes });
  };

  const filteredLineItems = useMemo(() => {
    if (!filterCriteria) return lineItems;

    return lineItems.filter(item => {
      if ('pims_ids' in filterCriteria) {
        return filterCriteria.pims_ids.includes(item.pims_id);
      }
      if ('is_voided' in filterCriteria) {
        return item.is_voided === filterCriteria.is_voided;
      }
      return (
        item.from_itp === filterCriteria.from_itp &&
        item.from_pvl === filterCriteria.from_pvl &&
        item.is_voided === filterCriteria.is_voided
      );
    });
  }, [lineItems, filterCriteria]);

  const isMedicalNote = item =>
    typeof item?.rule_id === 'string' &&
    item.rule_id.startsWith('mn');  

  const bulkJournalCandidates = useMemo(() => {
    const rows = [];
  
    // 1. Missed / Void (InvoiceTable rows)
    for (const item of lineItems) {
      const isMissedOrVoid =
        (item.from_itp && !item.from_pvl) || item.is_voided;
      if (!isMissedOrVoid) continue;
  
      const key = [
        headerVisit.unique_id,
        item.pims_id,
        normalizeDateKey(item.completed_date),
      ].join('|');
  
      if (journalIndex.byInvoiceKey.has(key)) continue;
  
      rows.push({
        journalType: 'invoice',
        source: item,
      });
    }
  
    // 2. Charge Capture & Medical Notes
    for (const item of captureData) {
      if (item.result !== 'FAIL') continue;
  
      if (
        isMedicalNote(item) &&
        journalIndex.byInvoiceItemId.has(item.rule_id)
      ) continue;
  
      if (
        !isMedicalNote(item) &&
        journalIndex.byLabel.has(item.title?.trim())
      ) continue;
  
      rows.push({
        journalType: isMedicalNote(item)
          ? 'medical_note'
          : 'charge_capture',
        source: item,
      });
    }
  
    // 3. Discounts (MATCH DiscountPanel logic exactly)
    for (const discount of discountData) {
      const gross = discount.gross_revenue || 0;
      const net = discount.net_revenue || 0;

      const discountAmount = discount.logic_checks?.length
        ? Math.max(...discount.logic_checks.map(lc => lc.price || 0))
        : gross - net;

      const base = {
        parent: {
          staffid: discount.staffid,
          datetime: discount.datetime,
          invoiceitemid: discount.invoiceitemid,
          gross_revenue: gross,
          net_revenue: net,
          discount_types: discount.discount_types,
          reason: discount.reason,
        },
        discountAmount,
      };

      const logicChecks = discount.logic_checks?.length
        ? discount.logic_checks.map(lc => ({
            ...lc,
            ...base,
          }))
        : [{
            title: 'Discount Applied',
            result: null,
            ...base,
          }];

      for (const item of logicChecks) {
        if (item.result !== 'FAIL') continue;

        const key = [
          headerVisit.unique_id,
          item.parent.invoiceitemid,
          normalizeDateKey(item.parent.datetime),
        ].join('|');

        if (journalIndex.byInvoiceKey.has(key)) continue;

        rows.push({
          journalType: 'discount',
          source: item,
        });
      }
    }
  
    return rows;
  }, [lineItems, captureData, discountData, journalIndex]);

  const calculateActualPercentLoss = (headerVisit) => {
    const totalLoss = Number(headerVisit.total_loss || 0);
    const voidTotal = Number(headerVisit.void_total || 0);
    const invoiceTotal = Number(headerVisit.invoice_total || 0);
  
    const actual_loss = totalLoss - voidTotal;
    const actual_total = invoiceTotal - voidTotal;
  
    if (actual_total <= 0) return '0.0%';
  
    const pct = (actual_loss / actual_total) * 100;
    return `${pct.toFixed(1)}%`;
  };
  
  const formattedPercentLoss = calculateActualPercentLoss(headerVisit);  

  const pvl = headerVisit.invoice_total - headerVisit.total_loss;
  const total_loss = headerVisit.total_loss - headerVisit.void_total;
  const invoice_total = headerVisit.invoice_total - headerVisit.void_total;
  const percent_loss = formattedPercentLoss;  

  if (loading) return <div>Loading visit details...</div>;


  return (
    <div className="visit-detail">
      <div className="content-wrapper">
        <div className="left-panel">
          <div className="visit-detail-header left-panel-header">
            
          <button
            onClick={() => navigate(-1)}
            className="header-back-button"
          >
            ← Back
          </button>

            <div className="visit-info">
              <h2>
                {headerVisit.patient_name} ({headerVisit.patient_pims_id})
              </h2>
              <p><strong>Hospital:</strong> {headerVisit.hospital_shortname}</p>
              <p><strong>Summary:</strong> {headerVisit.summary}</p>
              <p><strong>Unique ID:</strong> {unique_id}</p>
              
              {checkInInfo && (
                <div className="checkin-compact">
                  <Clock size={14} />
                  <span>
                    Checked in {checkInInfo.formattedDate}
                    {' '}•{' '}
                    <b>
                      {checkInInfo.decimalBlocks} 12h Blocks
                    </b>
                    {checkInInfo.remainingHours > 0 && (
                      <> + {checkInInfo.remainingHours}h</>
                    )}
                  </span>
                </div>
              )}

            </div>
          </div>

          <div className="tab-buttons">
            <button className={activeTab === 'notes' ? 'active' : ''} onClick={() => setActiveTab('notes')}>Notes</button>
            <button className={activeTab === 'charges' ? 'active' : ''} onClick={() => setActiveTab('charges')}>Charge Capture</button>
            <button className={activeTab === 'discounts' ? 'active' : ''} onClick={() => setActiveTab('discounts')}>Discounts</button>
            <button className={activeTab === 'journals' ? 'active' : ''} onClick={() => setActiveTab('journals')}>Journals</button>
          </div>

          <div className="tab-content">

          {activeTab === 'notes' && (
            <>
              <div className="search-container">
                <input
                  type="text"
                  className="med-note-search"
                  placeholder="Search Medical Notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {dropdownResults.length > 0 && (
                  <ul className="search-dropdown">
                    {dropdownResults.map((note, idx) => (
                      <li
                        key={idx}
                        className="search-snippet"
                        onClick={() => {
                          setModalNote(note);
                          setSearchTerm('');
                        }}
                        dangerouslySetInnerHTML={{ __html: note.snippet }}
                      />
                    ))}
                  </ul>
                )}
              </div>

              {medNotes.length === 0 ? (
                <p>No medical notes found for this visit.</p>
              ) : (
                <ul className="medical-notes-grid">
                  {medNotes.map((note, index) => (
                    <li
                      key={index}
                      className="medical-note"
                      onClick={() => setModalNote(note)}
                    >
                      <NoteHeaderContent item={note} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

            {activeTab === 'charges' && (
              captureData.length === 0
                ? <p>No charge capture data found for this visit.</p>
                : <ChargeCapturePanel 
                    captureData={captureData} 
                    setActiveInfoItem={setActiveInfoItem} 
                    visit={headerVisit} 
                    data={filteredLineItems} 
                    journalIndex={journalIndex}
                    refreshVisitDetails={fetchVisitDetails}                   
                  />
            )}

            {activeTab === 'discounts' && (
              discountData.length === 0
                ? <p>No discounts found for this visit.</p>
                : <DiscountPanel 
                    visit={headerVisit} 
                    discounts={discountData} 
                    journalIndex={journalIndex} 
                    refreshVisitDetails={fetchVisitDetails}        
                  />
            )}

            {activeTab === 'journals' && (
              journalData.length === 0
                ? <p>No journals found for this visit.</p>
                : <JournalPanel visit={headerVisit} journals={journalData} />
            )}

          </div>
        </div>

        <div className="invoice-section">
          <StatCardMinis
            pvl={pvl}
            invoice_total={invoice_total}
            total_loss={total_loss}
            percent_loss={percent_loss}
          />

          <StatCards
            missed_charge_amount={headerVisit.missed_charge_amount}
            discount_total={headerVisit.failed_discounts}
            charge_capture_total={headerVisit.charge_capture_total}
          />

          <InvoiceTable
            data={filteredLineItems}
            visitData={headerVisit}
            statusData={statusData}
            journalIndex={journalIndex}            
            updateCardStatusLocally={updateCardStatusLocally}
            refreshVisitDetails={fetchVisitDetails}
            onBulkJournal={() => setOpenBulkJournal(true)}          
          />
        </div>
      </div>

      <Modal
        style={{ padding: '.15rem' }}
        isOpen={!!modalNote}
        onClose={() => setModalNote(null)}
        title={modalNote ? <NoteModalContent item={modalNote} /> : ''}
      >
        {modalNote && (
          <div className="modal-note-text">
            {renderHighlightedText(
              modalNote.text_only,
              modalNote.highlights
            )}
          </div>
        )}

      </Modal>

      <BulkJournalModal
        open={openBulkJournal}
        onClose={() => setOpenBulkJournal(false)}
        visit={headerVisit}
        candidates={bulkJournalCandidates}
        refreshVisitDetails={fetchVisitDetails}
      />

      <ModernInfoPopup
        item={activeInfoItem}
        onClose={() => setActiveInfoItem(null)}
      />
    </div>
  );
};

export default VisitDetail;
